/**
 * @fileoverview About page with information about the spinner app
 */

import { Helmet } from 'react-helmet-async';
import { Heart, Users, Globe, Shield, Zap, Star, BookOpen, Brain, Target, Lightbulb } from 'lucide-react';
import { HelpPageLayout } from '../../components/help/HelpPageLayout';

const About: React.FC = () => {

  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Fast & Responsive",
      description: "Lightning-fast performance with smooth animations and instant results."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Multi-Language",
      description: "Available in 11 languages to serve users worldwide, and bringingg more."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Safe & Secure",
      description: "Family-friendly content with robust safety filters and privacy protection."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Education",
      description: "Promoting togetherness and learning while having fun - addressing the missing connection in modern era."
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Premium Quality",
      description: "Curated questions and high-quality content for the best experience."
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Made with Love",
      description: "Created by a team passionate about bringing people together through fun games."
    }
  ];



  return (
    <HelpPageLayout>
      <Helmet>
        <title>About - Spinner Games !!</title>
        <meta name="description" content="Learn about Spinner Games !! - the interactive spinner app that brings people together through fun games and decision-making tools." />
        <meta property="og:title" content="About - Spinner Games !!" />
        <meta property="og:description" content="Learn about our mission to create fun, engaging, and safe interactive games for everyone." />
        <link rel="canonical" href="https://spinnergames.fun/help/about" />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Spinner Games !!",
          "description": "Interactive spinner app for games and decision making",
          "url": "https://spinnergames.fun",
          "foundingDate": "2024",
          "numberOfEmployees": "5-10",
          "knowsAbout": ["Interactive Games", "Decision Making", "Party Games", "Team Building"]
        })}
        </script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center justify-center">
            <img src="/logo.png" alt="Spinner Games" className="w-8 h-8 mr-3 flex-shrink-0" />
            About Spinner Games !!
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-6">
            A digital platform designed to bring people together through interactive entertainment and educational activities
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
            <p className="text-lg text-gray-700 dark:text-gray-300 text-justify">
              Our mission is to facilitate meaningful connections between family and friends while providing valuable learning experiences that enhance social skills, knowledge, and interpersonal relationships. Whether you're planning a family game night, organizing a classroom activity, or looking for a creative way to make decisions, Spinner Games provides the tools you need to bring people together.
            </p>
          </div>
        </div>



        {/* Game Types Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center flex items-center justify-center">
            <img src="/logo.png" alt="Spinner Games" className="w-6 h-6 mr-3 flex-shrink-0" />
            Interactive Game Types
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300 mr-4">
                  <BookOpen className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Trivia</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Engaging quiz games that test and expand knowledge across various topics including history, science, entertainment, and more.
              </p>
              <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <li>• History, Science, Entertainment questions</li>
                <li>• Multiple difficulty levels</li>
                <li>• Educational and fun</li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center text-red-600 dark:text-red-300 mr-4">
                  <Heart className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Truth or Dare</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Classic party game that encourages openness and fun challenges, perfect for breaking the ice and building connections.
              </p>
              <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <li>• Age-appropriate questions and dares</li>
                <li>• Ice-breaker activities</li>
                <li>• Builds trust and friendship</li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-300 mr-4">
                  <Brain className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Which One Are You?</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Personality-based games that help you discover more about yourself and others through fun, revealing questions.
              </p>
              <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <li>• Personality discovery questions</li>
                <li>• Self-reflection activities</li>
                <li>• Learn about friends and family</li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center text-green-600 dark:text-green-300 mr-4">
                  <Target className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Would You Rather</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Decision-making games that spark interesting conversations and reveal preferences through thought-provoking choices.
              </p>
              <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <li>• Thought-provoking scenarios</li>
                <li>• Conversation starters</li>
                <li>• Reveals personal preferences</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center flex items-center justify-center">
            <img src="/logo.png" alt="Spinner Games" className="w-6 h-6 mr-3 flex-shrink-0" />
            What Makes Us Special
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center flex items-center justify-center">
            <img src="/logo.png" alt="Spinner Games" className="w-6 h-6 mr-3 flex-shrink-0" />
            Key Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300 mb-4">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Strengthens Relationships</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Build deeper connections with family and friends through shared experiences and meaningful conversations.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center text-green-600 dark:text-green-300 mb-4">
                <Lightbulb className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Educational Value</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Learn new facts, develop critical thinking skills, and expand knowledge through engaging trivia and questions.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-300 mb-4">
                <Heart className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Entertainment</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Enjoy hours of fun with carefully curated content designed to entertain and engage all age groups.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center text-orange-600 dark:text-orange-300 mb-4">
                <Brain className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Social Skills</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Develop communication skills, empathy, and social confidence through interactive gameplay.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center text-red-600 dark:text-red-300 mb-4">
                <Target className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Decision Making</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Practice making choices and expressing preferences in a fun, low-pressure environment.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center text-indigo-600 dark:text-indigo-300 mb-4">
                <Globe className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Global Community</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Connect with users worldwide through our multi-language platform and diverse content.
              </p>
            </div>
          </div>
        </div>

        {/* Story Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center flex items-center justify-center">
            <img src="/logo.png" alt="Spinner Games" className="w-6 h-6 mr-3 flex-shrink-0" />
            Our Story
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                Spinner Games !! was born from a simple idea: making decision-making fun and engaging. 
                We noticed that traditional decision-making tools were often boring or too complex, 
                and we wanted to change that.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                What started as a simple spinner has grown into a comprehensive platform with multiple 
                game types, international support, and a thriving community. We're proud to serve 
                users from around the world, helping them make decisions, have fun, and create 
                memorable moments together.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                Our commitment to quality, safety, and user experience drives everything we do. 
                We believe that technology should bring people together, not keep them apart, 
                and that's exactly what we're working to achieve.
              </p>
            </div>
          </div>
        </div>



        {/* Values Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Our Values
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Safety First
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                We prioritize user safety with robust content filtering, privacy protection, 
                and family-friendly content. Our platform is designed to be safe for users of all ages.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Quality Content
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Every question and game is carefully curated to ensure high quality, 
                engaging content that provides real value to our users.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                User Experience
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                We focus on creating intuitive, accessible, and enjoyable experiences 
                that work seamlessly across all devices and platforms.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Education & Togetherness
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                We believe in learning through play and fostering meaningful connections. 
                Our platform addresses the missing togetherness in the modern era while making education fun.
              </p>
            </div>
          </div>
        </div>

        {/* Mission Highlight */}
        <div className="text-center bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-8 mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center justify-center">
            <img src="/logo.png" alt="Spinner Games" className="w-6 h-6 mr-3 flex-shrink-0" />
            Why Spinner Games Matters Today
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 text-justify">
            In our increasingly digital world, genuine human connection has become more valuable than ever. Spinner Games addresses the growing need for meaningful interactions, educational entertainment, and social bonding in a world where technology often isolates rather than connects.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Globe className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                <span className="text-gray-700 dark:text-gray-300">Digital Connection Gap</span>
              </div>
              <div className="flex items-center space-x-3">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                <span className="text-gray-700 dark:text-gray-300">Social Skills Development</span>
              </div>
              <div className="flex items-center space-x-3">
                <Lightbulb className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                <span className="text-gray-700 dark:text-gray-300">Learning Through Play</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Heart className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                <span className="text-gray-700 dark:text-gray-300">Family Bonding</span>
              </div>
              <div className="flex items-center space-x-3">
                <Brain className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                <span className="text-gray-700 dark:text-gray-300">Critical Thinking</span>
              </div>
              <div className="flex items-center space-x-3">
                <Target className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                <span className="text-gray-700 dark:text-gray-300">Decision Making Skills</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center bg-blue-50 dark:bg-blue-900/20 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Get in Touch
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Have questions, suggestions, or just want to say hello? We'd love to hear from you!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="/help/contact"
              className="inline-flex items-center justify-center px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-[240px]"
            >
              Contact Us
            </a>
            <a
              href="/"
              className="inline-flex items-center justify-center px-8 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors w-full sm:w-[240px]"
            >
              Start Playing
            </a>
          </div>
        </div>
      </div>
    </HelpPageLayout>
  );
};

export default About; 